import xdk from '@accedo/xdk-core'
import isTizen from '@accedo/xdk-device-samsung-tizen/esm/detection'
import { Registry, Router } from '@lightningjs/sdk'
import { onValue, Unsubscribe } from 'firebase/database'
import { onSnapshot } from 'firebase/firestore'

import {
  NavigationPointers,
  PointerOrientation,
  VerticalPointerSize,
} from '../components/NavigationPointers/NavigationPointers'
import PointerBackButton from '../components/NavigationPointers/PointerBackButton'
import VideoPlayer from '../components/player'
import UpNext from '../components/player/upNext'
import { appendQueryParams, getUnifiedCdnPlayback } from '../components/player/utils'
import { VideoControlStates } from '../components/player/videoControl'
import TvEventCard from '../components/TvCards/TvEventCard'
import TvCarouselContainer from '../components/TvCarouselContainer/TvCarouselContainer'
import { transparent } from '../constants/colors'
import { Images } from '../constants/paths'
import { HOME, PAYWALL } from '../constants/routes'
import { PLAYBACK_SIGNALS, PLAYER_CONTROLS_SIGNALS } from '../constants/signals'
import { KEY_CODE } from '../constants/videoPlayer'
import { AppConfig } from '../services/appConfig'
import {
  EntitlementsResourceType,
  RenderableComponentMetadataEnum,
  TvCardTypesEnum,
} from '../types/flo-enums'
import {
  CardItemModel,
  FirebaseEventUpdate,
  PlayerModel,
  SportData,
  StreamItem,
  TvCarouselContainerModel,
  TvVideoCardModel,
} from '../types/flo-experience'
import { Flo } from '../types/flo-live-api'
import { getUserData, isUserSignedIn } from '../utils/authentication'
import { getUpdatedCardItems } from '../utils/cardUtils'
import {
  createDatadogErrorObj,
  createErrorException,
  logDatadogError,
  logDatadogInfo,
} from '../utils/datadog'
import { getText } from '../utils/dictionary'
import {
  areStreamsEqual,
  Firebase,
  getLatestDocs,
  streamIdsArr,
  streamsArr,
} from '../utils/firebase'
import { getAppName, getAppVersion, getDeviceInfo, getDeviceMake } from '../utils/info'
import { areObjsEqual, chunkArray, deepCopyProperties } from '../utils/misc'
import { captureScreenData, NON_BFF_SEGMENT_SCREENS } from '../utils/segmentAnalytics'
import { VizbeeStopReasons } from '../utils/vizbee/VizbeeReceiver'
import {
  getWatchHistoryItem,
  removeFromWatchHistory,
  saveInWatchHistory,
} from '../utils/watchHistory'
import { removeYouboraPlugin, setYouboraVideoOptions } from '../utils/youbora'
import PageHandler from './handlers/pageHandler'

/** Heartbeat refresh rate in milliseconds. */
const HEARTBEAT_REFRESH_INTERVAL = 10000
const HEARTBEAT_VIDEO_REFRESH_INTERVAL = 60000

/** How much time the no audio toast notification is visible in milliseconds. */
const NO_AUDIO_TOAST_TIME = 4000
const ERROR_DELAY = 500

const enum State {
  Playback = 'Playback',
  UpNext = 'UpNext',
}

export interface PlayerDataModel extends PlayerModel {
  subtitle?: string
}

export default class Player extends PageHandler {
  private _data: PlayerDataModel
  private _authDeeplinked: boolean
  private _heartbeatIntervalId = -1
  private _adsTrackingURL = null
  private _heartbeatTokenId = null
  private _heartbeatCounter = 0
  private _isMounted = false
  private _usingCleanUri = false
  private _playbackHeaders: Record<string, string> | undefined
  private _liveId: number = 0
  private _firebase: Firebase = new Firebase()
  private _eventListener: Unsubscribe
  private _sportDataListeners: Unsubscribe[] = []
  private _status: string
  private _latestStreams: StreamItem[] = []
  private _latestSportData: SportData[] = []
  private _playlistCarousel: TvCarouselContainer
  private _onVizbeeLoadedMetadata: Function
  private _videoUrl: string
  private _isUpNext = false // used for youbora analytics

  static _template() {
    return {
      w: 1920,
      h: 1080,
      rect: true,
      color: transparent,
      Player: {
        type: VideoPlayer,
        signals: {
          ...PLAYBACK_SIGNALS,
          saveToCW: true,
        },
      },
      UpNext: {
        type: UpNext,
        signals: PLAYER_CONTROLS_SIGNALS,
        visible: false,
      },
      NavigationPointers: {
        type: NavigationPointers,
        pointerOrientation: PointerOrientation.VERTICAL,
        verticalPointerSize: VerticalPointerSize.SMALL,
        pointerWidth: 1920,
      },
      PointerBackButton: {
        type: PointerBackButton,
        x: (w) => w - 96,
        y: 80,
        signals: {
          backFromPointerBackButton: true,
        },
      },
    }
  }

  // Note: Route's player path on function call this once
  set params(v: PlayerDataModel) {
    if (!v.analytics && !v.content_metadata) {
      return
    }

    this._data = v
    this._authDeeplinked = v?.authDeeplinked

    if (this._isMounted) {
      // Hackish solution to remove the widgets
      // For some reason jumping to the same page doesn't seem to call any of the other
      // events like _active, _enable, etc. and if we don't do the setTimeout the widgets
      // will be visible.
      setTimeout(() => {
        this.setModal(false)
        this.setSideModal(false)
        this.setToastNotification(false)
      }, 0)

      this.updateMetadata()
      this.open()
    }
  }

  historyState(v) {
    if (v) {
      this.params = v
    } else {
      return {
        authDeeplinked: this._authDeeplinked,
        ...this._data,
      }
    }
  }

  get data() {
    return this._data
  }

  get player() {
    return this.tag('Player') as VideoPlayer
  }

  get upNext() {
    return this.tag('UpNext') as UpNext
  }

  get controls() {
    return this.player?.controls
  }

  get currentPosition() {
    return Math.round(this.controls?.currentTime)
  }

  get navigationPointers() {
    return this.tag('NavigationPointers') as NavigationPointers
  }

  get pointerBackButton() {
    return this.tag('PointerBackButton') as PointerBackButton
  }

  async _onMounted() {
    this._isMounted = true
    if (this._data) {
      this.updateMetadata()
      this.open()
    }
  }

  _init() {
    // Init Player
    this._setState(State.Playback)

    this._firebase.init()

    void captureScreenData(NON_BFF_SEGMENT_SCREENS.VideoPlayer)

    this._onVizbeeLoadedMetadata = this.onVizbeeLoadedMetadata.bind(this)

    this.checkNavigationPointersVisibility(false)
  }

  _active() {
    this.setModal(false)
    this.setToastNotification(false)
    this.setSideModal(false)
  }

  _disable() {
    this._videoUrl = null
    Registry.clearInterval(this._heartbeatIntervalId)
    Registry.removeEventListener(
      this.player.videoElement,
      'loadedmetadata',
      this._onVizbeeLoadedMetadata,
    )
    removeYouboraPlugin()
  }

  _inactive() {
    if (this._eventListener) {
      this._eventListener()
    }

    this.stopListeningToSportsData()
  }

  _getFocused() {
    return this.state === State.Playback ? this.player : this.upNext
  }

  _focus() {
    this.setSideModal(false)
  }

  _handleBack(event?: any) {
    this.player.patch({ visible: false })
    this.upNext.patch({ visible: false })

    const { currentTime, duration } = this.player.player
    this.updateBookmark(currentTime, duration)
    this.saveToCW(currentTime, duration)

    this._isMounted = false

    event?.preventDefault?.()

    logDatadogInfo('Player stop', this._data)

    this.player.close()

    if (Router.getHistory().length === 0 || this._authDeeplinked) {
      Router.navigate(HOME, false)
    } else {
      Router.back()
    }

    return true
  }

  static _states() {
    return [
      class Playback extends this {
        $enter() {
          this.player.patch({ visible: true })
          this.upNext.patch({ visible: false })
          this.checkNavigationPointersVisibility(false)
        }
      },
      class UpNext extends this {
        $enter() {
          this.player.patch({ visible: false })
          this.upNext.patch({ visible: true })
          this.checkNavigationPointersVisibility(false)
        }
      },
    ]
  }

  async open(useCleanUri = false) {
    // Check entitlements
    this._liveId = this._data?.analytics?.extra_context?.live_id
    const content = this._data.content_metadata
    let resourceId: string | number = this._liveId
    let resourceType = EntitlementsResourceType.LIVE_EVENT
    if (!resourceId) {
      resourceType = EntitlementsResourceType.NODE
      resourceId = content.node_id
    }

    if (content.is_premium) {
      const isEntitled = await AppConfig.shared.authService.checkEntitlements({
        resourceType,
        resourceId,
      })

      if (!isEntitled) {
        this.player.close()
        Router.navigate(PAYWALL, { siteId: content.site_id })
        return
      }
    }

    // Prepare url for playback
    let url = ''
    let streamId = -1
    this._playbackHeaders = undefined

    const type = content.type

    const { device } = AppConfig.shared.appSettings
    const os = device.getOS()
    const mediaTailor: Flo.MediaTailorParams = {
      appName: getAppName(),
      appVersion: getAppVersion(),
      app_store_url: device.getAppStoreURL(),
      deviceModel: getDeviceInfo().modelNumber,
      rdid: device.getRDID(),
      isLat: device.isLATEnabled() ? 1 : 0,
      height: window.screen.height,
      width: window.screen.width,
      os: os.name,
      osVersion: os.version,
    }

    if (type === RenderableComponentMetadataEnum.Vod) {
      url = appendQueryParams(content.playlist, mediaTailor)
      this.player.isLiveStream = false
    } else if (type === RenderableComponentMetadataEnum.Event) {
      streamId = this._data.content_metadata.streams?.[0]?.stream_id
    } else if (type === RenderableComponentMetadataEnum.Stream) {
      streamId = this._data.content_metadata.stream_id
    }

    if (streamId >= 0) {
      this.player.isLiveStream = true
      const sd = await AppConfig.shared.liveService.getPremiumStream({
        streamId: streamId,
        mediaTailor,
      })

      if ('error' in sd) {
        setTimeout(() => {
          this.setModal(true, {
            title: getText('playerConnectionErrorTitle'),
            info: getText('playerConnectionErrorSubtitle'),
            buttons: [
              {
                label: getText('reload'),
                callback: () => {
                  this.setModal(false)
                  this.player.reset()
                  this.open()
                  this._setState(State.Playback)
                  Router.focusPage()
                },
              },
              {
                label: getText('exit'),
                callback: () => {
                  Router.navigate(HOME)
                },
              },
            ],
          })
        }, ERROR_DELAY)

        return
      }

      const playback = getUnifiedCdnPlayback(sd.v2, useCleanUri ? 1 : 0)
      url = playback.url
      this._playbackHeaders = playback.headers
      this._heartbeatTokenId = sd.token?.id
      // Heartbeat setup

      // Clear timer if we already have one
      if (this._heartbeatTokenId >= 0) {
        Registry.clearInterval(this._heartbeatIntervalId)
      }

      this._heartbeatIntervalId = Registry.setInterval(
        this.sendHeartbeat.bind(this),
        HEARTBEAT_REFRESH_INTERVAL,
      )
    }

    this.updatePlaylistData()

    if (this._liveId && this.player.isLiveStream) {
      this.listenToStreamChanges(this._liveId)
    }

    this._videoUrl = url
    this.player.authDeeplinked = this._authDeeplinked
    if (!this._isMounted) return

    await this.player.open(url, { headers: this._playbackHeaders })

    await setYouboraVideoOptions({
      data: this._data,
      isLive: this.player.isLiveStream,
      resourceUrl: this.player.src,
      isUpNext: this._isUpNext,
    })
  }

  restart() {
    this.controls.showControls()
    this.open()
  }

  videoStart() {
    logDatadogInfo('Player start', this._data)
    this.setupVizbeeRemoteControls()

    // Note: Get initial seek values here
    const watchHistoryItem = getWatchHistoryItem(this._data?.node_id)

    let initialSeek = undefined

    if (!this.player.isLiveStream) {
      initialSeek = watchHistoryItem?.progress ?? this._data?.content_metadata?.progress_seconds
    }

    const duration = 0

    this.controls.duration = duration
    this.controls.currentTime = initialSeek

    this.player.play(initialSeek)
    this.setLoadingAnimation(false)
    if (this._data?.content_metadata?.no_audio) {
      this.setToastNotification(true, {
        icon: Images.PLAYER_NO_AUDIO,
        message: getText('playerNoAudioNotificationMessage'),
        timeout: NO_AUDIO_TOAST_TIME,
      })
    }
  }

  async videoEnd() {
    logDatadogInfo('Player stop', this._data)

    // If we're at the end then we send continue watching request to track progress at 100%
    this.updateBookmark(this.controls.duration, this.controls.duration, true)
    this.saveToCW(this.controls.duration, this.controls.duration)

    await this.player.reset()

    // Go back if we're not playing a playlist or if we reached the end of the playlist
    const playlist = (this.controls.upNextCarousel.data as TvCarouselContainerModel[])?.[0]
    if (!playlist?.items?.length) {
      // If it is deeplink we go back to the home page
      if (Router.getHistory().length === 0 || this._authDeeplinked) {
        Router.navigate(HOME, false)
      } else {
        Router.back()
      }
      return
    }

    this.upNext.restartTimer()
    this.upNext.playlist = (this.controls.upNextCarousel.data as TvCarouselContainerModel[])?.[0]

    this._setState(State.UpNext)
  }

  async videoError(data) {
    logDatadogError(createDatadogErrorObj(this._data), createErrorException(data))

    if (!this._usingCleanUri) {
      this._usingCleanUri = true
      console.log('Failed to play original URL, switching to a different stream URL.')
      await this.player.reset()
      this.open(true)
    } else {
      this._usingCleanUri = false

      console.error('Failed to play video.')

      this.setModal(true, {
        title: getText('playerConnectionErrorTitle'),
        info: getText('playerConnectionErrorSubtitle'),
        buttons: [
          {
            label: getText('reload'),
            callback: () => {
              this.setModal(false)
              this.player.reset()
              this.open()
              this._setState(State.Playback)
              Router.focusPage()
            },
          },
          {
            label: getText('exit'),
            callback: () => {
              Router.navigate(HOME)
            },
          },
        ],
      })
    }
  }

  async jumpNext() {
    // Set state to remove up next screen
    this._setState(State.Playback)

    this._isUpNext = true

    const playlist = (this.controls.upNextCarousel.data as TvCarouselContainerModel[])?.[0]
    this._data = { ...(playlist.items[0].action as any), playlist }
    this.updateMetadata()

    this.controls.hideControls()
    this.open()

    // If the UpNext countdown reaches 0, the player route is not reloaded, hence the _init() method inside VideoControls will not execute again,
    // so we need to explicitly attach here the mousemove event to the window so that whenever the user moves the cursor, the controls appear. This
    // does not happen when a user directly clicks on a video card as this will call the Router.navigate() method and reload the route
    this.controls.attachMouseMoveEvent()
  }

  sendHeartbeat() {
    if (this._heartbeatCounter >= HEARTBEAT_VIDEO_REFRESH_INTERVAL) {
      AppConfig.shared.liveService
        .sendLiveHeartbeat({ tokenId: this._heartbeatTokenId })
        .catch((err) => {
          this.player.close()
          this.setModal(true, {
            info: err,
            buttons: [
              {
                label: getText('tryAgainButton'),
                callback: () => {
                  Router.back()
                },
              },
              {
                label: getText('exit'),
                callback: () => {
                  AppConfig.shared.onShutdown()
                },
              },
            ],
          })
          Router.focusWidget('Modal')
        })

      this._heartbeatCounter = -HEARTBEAT_REFRESH_INTERVAL
    }

    this._heartbeatCounter += HEARTBEAT_REFRESH_INTERVAL
  }

  updateMetadata() {
    this.controls.metadata = {
      title:
        this._data.content_metadata?.short_title ??
        this._data.content_metadata?.title ??
        this._data.analytics?.video_analytics?.event_name,
      subtitle: this._data.subtitle,
      data: this._data,
    }
  }

  updatePlaylistData() {
    const data = this.data

    if (data == null || data.playlist == null) {
      console.log('Data null')
      return
    }

    if (Object.keys(data.playlist).length === 0) {
      return
    }

    // Remove current item from playlist
    if (data.playlist) {
      const isLive = this.player.isLiveStream
      const playlist = data.playlist as TvCarouselContainerModel
      let newPlaylist = {} as TvCarouselContainerModel
      const items = playlist.items as TvVideoCardModel[]
      let filteredItems = []
      // filter see all card
      filteredItems = items.filter((item) => item.type !== TvCardTypesEnum.TvPaginationCard)

      if (isLive) {
        deepCopyProperties(playlist, newPlaylist)
        filteredItems = filteredItems.filter(
          (item) => item.action?.analytics?.stream_id !== data.analytics?.stream_id,
        )
      } else {
        filteredItems = filteredItems.filter((item) => item.action?.node_id !== data.node_id)
      }

      newPlaylist.items = filteredItems

      if (newPlaylist.items.length > 0) {
        this.controls.upNextCarousel.visible = true
        this.controls.upNextCarousel.data = [newPlaylist]
        this.controls.upNextCarousel.playlist = data?.playlist
        this.controls.upNextCarousel.isLive = isLive
        this.controls.upNextCarousel.title = getText(
          isLive ? 'playerCarouselLiveTitle' : 'playerCarouselVodTitle',
        )
      } else {
        this.controls.upNextCarousel.visible = false
        this.controls.upNextCarousel.data = []
      }
    }
  }

  updateBookmark(currentTime: number, duration: number, completed = false) {
    // Save progress of video if not a live stream
    if (!this.player.isLiveStream && isUserSignedIn()) {
      const progress = currentTime > duration ? duration : currentTime
      const nodeId = this._data?.node_id

      if (!completed) {
        saveInWatchHistory({ nodeId, progress })
      } else {
        removeFromWatchHistory(nodeId)
      }

      AppConfig.shared.continueWatchingService.saveProgress({
        userId: getUserData().user_id,
        nodeId: nodeId,
        progressSeconds: progress,
        videoTotalSeconds: duration,
      })
    }
  }

  scoresModal() {
    this.openSideModal({
      title: getText('playerScoresButton'),
      description: getText('playerScoresDescription'),
      url: `${this._data.web_game_center_url}?utm_source=${xdk.platform}`,
    })
  }

  bracketsModal() {
    this.openSideModal({
      title: getText('playerBracketsButton'),
      description: getText('playerBracketsDescription'),
      url: `${this._data.web_brackets_url}?utm_source=${xdk.platform}`,
    })
  }

  feedbackModal() {
    this.openSideModal({
      title: getText('playerFeedbackModalTitle'),
      description: getText('playerFeedbackDescription'),
      url: this.getFeedbackUrl(),
      footer: [getText('playerFeedbackFooterText'), getText('playerFeedbackFooterLink')],
    })
  }

  getFeedbackUrl() {
    const { device } = AppConfig.shared.appSettings
    const userData = getUserData()

    // converting some numbers to string or else URLSearchParams below complains
    const params = {
      event_id: `${this._data.analytics?.entity_id}`,
      node_id: `${this._data.node_id}`,
      stream_id: this._data.content_metadata?.stream_id,
      user_id: `${userData?.user?.id}`,
      email: userData?.user?.email,
      vertical: this._data.analytics?.video_analytics?.vertical_name,
      event_title: this._data.analytics?.video_analytics?.event_name || this._data.title,
      stream_title: this._data.content_metadata?.stream_name,
      app_name: getAppName(),
      app_version: getAppVersion(),
      device_model: getDeviceInfo().modelNumber,
      device_make: getDeviceMake(),
      os: xdk.platform?.toLowerCase(),
      os_version: device.getOS()?.version,
    }

    const url = new URL(getText('playerFeedbackUrl'))
    const searchParams = new URLSearchParams(params)
    url.search = searchParams.toString()

    return url.toString()
  }

  openSideModal(data: SideModalParams) {
    this.controls.hideControls()
    this.setSideModal(true, data)
    Router.focusWidget('SideModal')
  }

  listenToStreamChanges(liveId: number) {
    this._eventListener = onValue(
      this._firebase.eventDbRef(liveId),
      (snapshot) => {
        const data = snapshot.val()

        if (data) {
          this.onFirebaseChange(data, liveId)
        }
      },
      (error) => {
        console.error(error)
      },
    )
  }

  onFirebaseChange(data: FirebaseEventUpdate, liveId: number) {
    if (!data?.streams || data?.status_v2 === 'CONCLUDED') {
      this.player.patch({ visible: false })
      this.upNext.patch({ visible: false })

      this._isMounted = false

      logDatadogInfo('Player stop', this._data)

      this.player.close()

      if (history.length === 0) {
        Router.navigate(HOME)
      } else {
        Router.back()
      }
    }

    if (data?.streams && data?.status_v2 === 'LIVE') {
      this._status = data?.status_v2
      let previousStreams = this._latestStreams
      this._latestStreams = streamsArr(data?.streams)?.filter(
        (s) => s.id !== this._data?.content_metadata?.stream_id,
      )

      if (!areStreamsEqual(previousStreams, this._latestStreams)) {
        this.updatePlaylistCarousel()
        this.listenToSportsData(this._latestStreams, liveId)
      }
    }
  }

  listenToSportsData(streams, liveId) {
    this.stopListeningToSportsData()

    const firebaseMaxWhereIn = 10
    const slices = chunkArray(streams, firebaseMaxWhereIn)

    slices.forEach((slice) => {
      const ids = streamIdsArr(slice)
      const q = this._firebase.query(ids, liveId)

      ids.forEach(() => {
        const unsub = onSnapshot(q, (snapshot) => {
          if (snapshot !== null) {
            const data = getLatestDocs(snapshot)
            let previousData = this._latestSportData
            this._latestSportData = data

            if (!areObjsEqual(previousData, this._latestSportData)) {
              this.processSportsData(data)
            }
          }
        })

        this._sportDataListeners.push(unsub)
      })
    })
  }

  stopListeningToSportsData() {
    if (this._sportDataListeners.length > 0) {
      this._sportDataListeners.forEach((unsub: Unsubscribe) => unsub?.())
    }
  }

  updatePlaylist(items: CardItemModel[]) {
    try {
      this._playlistCarousel.updateCards(items)
    } catch (e) {
      console.error(e)
    }
  }

  processSportsData(sportData: SportData[]) {
    sportData.forEach((data: SportData) => {
      const card = this._playlistCarousel.scrollContainer.children.find(
        (item: TvEventCard) => item.id === data.identity.liveStreamId,
      ) as TvEventCard

      if (card) {
        card.updateSportsData(data)
      }
    })
  }

  updatePlaylistCarousel() {
    this.controls.upNextCarousel.visible = true

    const playlistCarousel = this.controls.upNextCarousel.carouselsContainer
      ?.children?.[0] as TvCarouselContainer

    if (playlistCarousel) {
      this._playlistCarousel = playlistCarousel

      const updatedStreams = getUpdatedCardItems(
        this._latestSportData,
        this._latestStreams,
        this._status,
        this._data,
        playlistCarousel,
      )

      this.updatePlaylist(updatedStreams)
    }
  }

  onVizbeeLoadedMetadata() {
    const { vizbeeReceiver } = AppConfig.shared
    const currentTime = this.player.player.currentTime
    const imgUrl = this._data.imgUrl
    void vizbeeReceiver.updateVideoInfo(this._data, currentTime, imgUrl, this._videoUrl)
  }

  setupVizbeeRemoteControls() {
    // Need to pass the player element reference to Vizbee so playback can be controlled from mobile app
    const { vizbeeReceiver } = AppConfig.shared
    const vzbPlayerAdapter = vizbeeReceiver.createVizbeePlayerAdapter(this.player.videoElement)

    vzbPlayerAdapter.setPlayHandler(() => {
      if (this.controls?.state === VideoControlStates.ControlButtons) {
        this.controls?.controlButtons?._handleKey({ keyCode: KEY_CODE.MEDIA_PLAY })
      } else {
        this.controls?._handleKey({ keyCode: KEY_CODE.MEDIA_PLAY })
      }
    })
    vzbPlayerAdapter.setPauseHandler(() => {
      if (this.controls?.state === VideoControlStates.ControlButtons) {
        this.controls?.controlButtons?._handleKey({ keyCode: KEY_CODE.MEDIA_PAUSE })
      } else {
        this.controls?._handleKey({ keyCode: KEY_CODE.MEDIA_PAUSE })
      }
    })
    vzbPlayerAdapter.setSeekHandler((timeMs) => {
      const positionInSeconds = timeMs / 1000
      this.player.seek(positionInSeconds)
    })
    vzbPlayerAdapter.setStopHandler((reason) => {
      console.log({ reason })
      if (
        reason !== VizbeeStopReasons.STOP_IMPLICIT &&
        reason != VizbeeStopReasons.STOP_REASON_BEFORE_NEXT_VIDEO
      ) {
        this._handleBack()
      }
    })

    vizbeeReceiver.setVizbeePlayerAdapter(vzbPlayerAdapter)
    Registry.addEventListener(
      this.player.videoElement,
      'loadedmetadata',
      this._onVizbeeLoadedMetadata,
    )
  }

  stop() {
    this._handleBack()
  }

  $playlistItemSelected() {
    const { currentTime, duration } = this.player.player
    this.updateBookmark(currentTime, duration)
  }

  $upMovement() {
    this.controls._setState(VideoControlStates.Ranges)
    this.checkNavigationPointersVisibility(false)
  }

  $downMovement() {
    this.controls._setState(VideoControlStates.Carousel)
    this.checkNavigationPointersVisibility(true)
  }

  checkNavigationPointersVisibility(isCarouselFocused, areControlsVisible = undefined) {
    const { isCursorVisible } = AppConfig.shared.appSettings

    areControlsVisible = areControlsVisible ?? this.controls?.state !== VideoControlStates.Hidden
    const isUpNextCountdown = this.state === State.UpNext
    const isUpNextCarouselEmpty = !this.controls?.upNextCarousel?.visible
    const showTopPointer = isCarouselFocused
    const showBottomPointer = !isCarouselFocused && !isUpNextCarouselEmpty
    const isScrubbing = this.controls?.isScrubMode || this.controls?.isScrubbing

    this.navigationPointers.visible = isCursorVisible
    this.navigationPointers.showTopPointer =
      showTopPointer && !isUpNextCountdown && areControlsVisible && isCursorVisible
    this.navigationPointers.showBottomPointer =
      showBottomPointer && !isUpNextCountdown && areControlsVisible && isCursorVisible

    this.pointerBackButton.visible =
      (areControlsVisible || isScrubbing || isUpNextCountdown) && isCursorVisible
    this.widgets.sidemodal.pointerBackButton.visible = isCursorVisible
  }

  $toggleControlsVisibility(areControlsVisible) {
    const isCarouselFocused = this.controls?.state === VideoControlStates.Carousel
    this.checkNavigationPointersVisibility(isCarouselFocused, areControlsVisible)
  }

  $upNextCarouselFocused(isCarouselFocused) {
    this.checkNavigationPointersVisibility(isCarouselFocused)
  }

  _handleScroll({ deltaY }) {
    const isGoingDown = deltaY > 0
    const isUpNextCountdown = this.state === State.UpNext
    const isUpNextCarouselEmpty = !this.controls.upNextCarousel.visible
    const areControlsHidden = this.controls?.state === VideoControlStates.Hidden

    if (isUpNextCountdown || isUpNextCarouselEmpty || areControlsHidden) {
      return
    }

    this.controls._setState(isGoingDown ? VideoControlStates.Carousel : VideoControlStates.Ranges)
    this.checkNavigationPointersVisibility(isGoingDown)
  }

  _onCursorEnter() {
    const isUpNextCountdown = this.state === State.UpNext
    const isCarouselFocused = this.controls?.state === VideoControlStates.Carousel
    const scrubTrickplay = this.controls?.ranges?.scrubTrickplay
    this.checkNavigationPointersVisibility(isCarouselFocused)

    if (isUpNextCountdown) {
      this.upNext.checkNavigationPointersVisibility(true)
    } else if (isCarouselFocused) {
      this.controls?.checkNavigationPointersVisibility(true)
    }

    if (scrubTrickplay?.playPauseButton) {
      scrubTrickplay.playPauseButton.visible = true
    }
  }

  _onCursorLeave() {
    const isUpNextCountdown = this.state === State.UpNext
    const isCarouselFocused = this.controls?.state === VideoControlStates.Carousel
    const isProgressBarFocused = this.controls?.state === VideoControlStates.Ranges
    const areControlsFocused = this.controls?.state === VideoControlStates.ControlButtons
    const scrubTrickplay = this.controls?.ranges?.scrubTrickplay

    this.checkNavigationPointersVisibility(isCarouselFocused, false)

    if (isUpNextCountdown) {
      this.upNext.checkNavigationPointersVisibility(false)
      this.upNext.upNextCarousel?.currentContainer?.currentCard?._focus()
    } else if (isCarouselFocused) {
      this.controls?.checkNavigationPointersVisibility(false)
      this.controls?.upNextCarousel?.currentContainer?.currentCard?._focus()
    } else if (isProgressBarFocused) {
      this.controls?.ranges?.progressBar?._focus()
    } else if (areControlsFocused) {
      this.controls?.controlButtons?.focusedButton?._focus()
    }

    if (scrubTrickplay?.playPauseButton) {
      scrubTrickplay.playPauseButton.visible = false
    }
  }

  backFromPointerBackButton() {
    const isProgressBarFocused = this.controls?.state === VideoControlStates.Ranges
    const isScrubbing = this.controls?.isScrubMode || this.controls?.isScrubbing

    if (isProgressBarFocused && isScrubbing) {
      ;(this.controls as any)?._handleBack?.()
      this.$togglePointerBackButtonLabel(false)
    } else {
      this._handleBack()
    }
  }

  $togglePointerBackButtonLabel(showCancelLabel = false) {
    //@ts-ignore
    this.pointerBackButton.label = showCancelLabel
      ? getText('cancelButton')
      : getText('goBackButton')
    this.pointerBackButton.icon.visible = !showCancelLabel
  }

  saveToCW(currentTime: number, duration: number) {
    if (!isTizen || this.player.isLiveStream || currentTime < 10) {
      return
    }

    const { samsungCWService } = AppConfig.shared

    if (currentTime < duration * 0.95) {
      samsungCWService.addOrUpdateItem(this._data, currentTime)
    } else {
      samsungCWService.deleteItem(this.data)
    }
  }
}
